import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ConfirmModal from '../components/ConfirmModal';
import Button from '../components/Button';
import CampImportPanel from '../components/CampImportPanel';
import CampPriceOptionsEditor from '../components/camps/CampPriceOptionsEditor';
import CampPageContentEditor from '../components/camps/CampPageContentEditor';
import { dollarsToCents, centsToDollars } from '../lib/money';
import { isAthlete as isAthleteRole } from '../utils/roles';
import { useCatalog } from '../context/CatalogContext';
import { RailFrame, AdminWindowSwitcher } from '../components/AdminShell';
import { fmtRange, offsetLabel, FALLBACK_TZ } from '../components/camps/campReviewFormat';
import './CampsPage.css';
import {
  EditorHero,
  PageHeader,
  MonoLabel,
  SectionCap,
  EditorialButton,
  Pill,
  EmptyState,
  DateTile,
  Field,
} from '../components/Editorial';

const DATETILE_VARIANTS = ['orange', 'blue', 'green', 'purple', 'brown', 'red', 'ink'];
const datetileVariant = (id) => {
  const n = typeof id === 'number' ? id : (id ? id.toString().split('').reduce((s, c) => s + c.charCodeAt(0), 0) : 0);
  return DATETILE_VARIANTS[n % DATETILE_VARIANTS.length];
};

// Camps are national; each camp's date is anchored to its own IANA timezone and
// rendered in that zone (with abbreviation) for every viewer.
const US_TIMEZONES = [
  { id: 'America/New_York', label: 'Eastern (ET)' },
  { id: 'America/Chicago', label: 'Central (CT)' },
  { id: 'America/Denver', label: 'Mountain (MT)' },
  { id: 'America/Phoenix', label: 'Arizona (no DST)' },
  { id: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { id: 'America/Anchorage', label: 'Alaska (AKT)' },
  { id: 'Pacific/Honolulu', label: 'Hawaii (HT)' },
];
const BROWSER_TZ = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago'; }
  catch { return 'America/Chicago'; }
})();

// "Sun, Jun 21, 2026" rendered in the camp's timezone (falls back to viewer-local
// for legacy camps with no stored tz).
const fmtCampDate = (isoStr, tz) => {
  if (!isoStr) return '';
  const opts = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };
  if (tz) opts.timeZone = tz;
  return new Date(isoStr).toLocaleDateString('en-US', opts);
};
// "8:00 AM CDT" in the camp's timezone.
const fmtCampTime = (isoStr, tz) => {
  if (!isoStr) return '';
  const opts = { hour: 'numeric', minute: '2-digit' };
  if (tz) { opts.timeZone = tz; opts.timeZoneName = 'short'; }
  return new Date(isoStr).toLocaleTimeString('en-US', opts);
};
// Month abbreviation + day number for the DateTile, in the camp's timezone.
const campTileParts = (isoStr, tz) => {
  if (!isoStr) return { month: '—', day: '—' };
  const d = new Date(isoStr);
  const opts = tz ? { timeZone: tz } : {};
  return {
    month: d.toLocaleDateString('en-US', { ...opts, month: 'short' }).toUpperCase(),
    day: d.toLocaleDateString('en-US', { ...opts, day: 'numeric' }),
  };
};

export default function CampsPage() {
  const { user, API_URL } = useAuth();
  const { sports } = useCatalog();
  const [searchParams, setSearchParams] = useSearchParams();
  const [camps, setCamps] = useState([]);
  const [myRegistrations, setMyRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCamp, setEditingCamp] = useState(null);
  const [campForm, setCampForm] = useState({
    name: '', description: '', location: '', venue: '',
    date: '', end_date: '', price_dollars: '', capacity: '',
    event_type: 'camp', timezone: BROWSER_TZ, sport_slug: '',
    slug: '', free_sub_months: 0, public_registration_enabled: false,
    ath_option_enabled: false,
  });

  const [confirmAction, setConfirmAction] = useState(null);
  const [lastVerifiedAt, setLastVerifiedAt] = useState(null);

  const [regsCamp, setRegsCamp] = useState(null); // camp whose registrations are open
  const [regs, setRegs] = useState([]);
  const EMPTY_WALKIN = { first_name: '', last_name: '', email: '', graduation_year: '', high_school: '', position: '', jersey_number: '' };
  const [walkIn, setWalkIn] = useState(null); // null = form hidden; object = form shown
  const [showImport, setShowImport] = useState(false); // roster import panel (admin only)
  const [jerseyResetNonce, setJerseyResetNonce] = useState(0);

  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });

  // Admin/staff/partner table view (rail roles only).
  // The review page links back with ?when=past.
  const [when, setWhen] = useState(() => (searchParams.get('when') === 'past' ? 'past' : 'upcoming'));   // upcoming | past
  const [kind, setKind] = useState('all');        // all | camp | clinic
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [campFind, setCampFind] = useState('');
  const [loadError, setLoadError] = useState(false);
  const [needsReview, setNeedsReview] = useState(false); // admin past: only camps with lost/warning flags
  // Per-camp review signals for the admin Past table: null until loaded (or on failure).
  const [reviewSummary, setReviewSummary] = useState(null);
  const summaryRequested = useRef(false);
  // Keep Past in the URL (replace, no new history entry) so browser Back from the review page
  // lands on Past again, not Upcoming.
  const changeWhen = (v) => {
    setWhen(v);
    const next = new URLSearchParams(searchParams);
    if (v === 'past') next.set('when', 'past'); else next.delete('when');
    setSearchParams(next, { replace: true });
  };

  const navigate = useNavigate();
  const isAdmin = user && (user.role === 'admin' || user.role === 'internal_staff');
  // Import is admin-only — narrower than isAdmin, which includes internal_staff.
  // Every /import endpoint gates on role == "admin" (camp_import.py:require_admin)
  // and 403s for staff, so reusing isAdmin here would show staff a button that
  // fails. Bulk account creation with an email/SMS blast attached is a bigger
  // lever than check-in or walk-ins.
  const isStrictAdmin = user?.role === 'admin';
  const isPartner = user?.role === 'partner';
  const isCoach = user?.role === 'coach';
  const isAthlete = isAthleteRole(user?.role);

  const getAxiosConfig = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    fetchCamps();
    fetchMyRegistrations();
  }, []);

  useEffect(() => {
    if (isAthlete) {
      axios.get(`${API_URL}/athletes/profile`, getAxiosConfig())
        .then(res => setLastVerifiedAt(res.data?.last_verified_at))
        .catch(() => {});
    }
  }, []);

  // Handle Stripe redirect
  useEffect(() => {
    const registered = searchParams.get('registered');
    const sessionId = searchParams.get('session_id');

    if (registered === 'success' && sessionId) {
      confirmRegistration(sessionId);
    } else if (registered === 'canceled') {
      showMessage('Registration canceled', 'warning');
    }
  }, [searchParams]);

  // F3: admin global search links a camp result to `/camps?camp=<id>`, but
  // this page previously only read `registered`/`session_id` — the `camp`
  // param was silently dropped and the admin landed on the unfiltered list
  // with no sign their selection was honored. Once camps have loaded,
  // surface the matching camp by scrolling it into view and highlighting it.
  const campParam = searchParams.get('camp');
  const [highlightedCampId, setHighlightedCampId] = useState(null);
  useEffect(() => {
    if (!campParam || camps.length === 0) return;
    const id = Number(campParam);
    if (camps.some((c) => c.id === id)) setHighlightedCampId(id);
  }, [campParam, camps]);

  // Admin Past table signals: one GET /camps/review-summary per page load, the first time an
  // admin views Past. Partners and Upcoming never ask. On failure the rows render with —.
  useEffect(() => {
    if (!isAdmin || when !== 'past' || summaryRequested.current) return;
    summaryRequested.current = true;
    axios.get(`${API_URL}/camps/review-summary`, getAxiosConfig())
      .then((res) => setReviewSummary(res.data && res.data.camps ? res.data : null))
      .catch((e) => console.error('Error fetching camp review summary:', e));
  }, [isAdmin, when]);

  // The review page's Registrations / Edit pills land here as ?open=registrations:<id> or
  // ?open=edit:<id>. Read it once the camps are loaded, open that modal, then drop the param
  // (replace, so Back returns to the review page rather than reopening the modal).
  const openHandled = useRef(false);
  useEffect(() => {
    if (loading || openHandled.current) return;
    openHandled.current = true;
    const open = searchParams.get('open');
    if (!open) return;
    const [what, idStr] = open.split(':');
    const target = camps.find((c) => c.id === Number(idStr));
    if (target && what === 'registrations' && isAdmin) openRegistrations(target);
    if (target && what === 'edit' && (isAdmin || (isPartner && target.created_by === user?.id))) handleEditCamp(target);
    const next = new URLSearchParams(searchParams);
    next.delete('open');
    setSearchParams(next, { replace: true });
  }, [loading, camps]);

  const fetchCamps = async () => {
    try {
      if (isAdmin || isPartner) {
        // The table view has an Upcoming | Past switch, so it needs every camp.
        const response = await axios.get(`${API_URL}/camps?include_past=true`, getAxiosConfig());
        setCamps(response.data);
        setLoadError(false);
      } else {
        const response = await axios.get(`${API_URL}/camps`, getAxiosConfig());
        const now = new Date();
        const activeCamps = response.data.filter(camp => {
          const campEnd = new Date(camp.end_date || camp.date);
          return campEnd >= now;
        });
        setCamps(activeCamps);
      }
    } catch (error) {
      console.error('Error fetching camps:', error);
      setLoadError(true);
    }
    setLoading(false);
  };

  const fetchMyRegistrations = async () => {
    try {
      const response = await axios.get(`${API_URL}/camps/my-registrations`, getAxiosConfig());
      setMyRegistrations(response.data);
    } catch (error) {
      console.error('Error fetching registrations:', error);
    }
  };

  const confirmRegistration = async (sessionId) => {
    try {
      await axios.post(
        `${API_URL}/camps/confirm-registration`,
        { session_id: sessionId },
        getAxiosConfig()
      );
      showMessage('Registration confirmed! You are signed up for the camp.', 'success');
      fetchCamps();
      fetchMyRegistrations();
    } catch (error) {
      console.error('Error confirming registration:', error);
      showMessage('Error confirming registration', 'error');
    }
  };

  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), type === 'error' ? 4000 : 3000);
  };

  const handleRegister = async (campId) => {
    try {
      const response = await axios.post(
        `${API_URL}/camps/${campId}/register`,
        {},
        getAxiosConfig()
      );
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        showMessage(response.data.message || 'Registered successfully!', 'success');
        fetchCamps();
        fetchMyRegistrations();
      }
    } catch (error) {
      showMessage(error.response?.data?.detail || 'Error registering', 'error');
    }
  };

  const handleCancelRegistration = (registrationId, campName) => {
    setConfirmAction({
      title: 'Cancel Registration',
      message: `Are you sure you want to cancel your registration for ${campName}?`,
      confirmLabel: 'Cancel Registration',
      danger: true,
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          await axios.post(
            `${API_URL}/camps/cancel-registration/${registrationId}`,
            {},
            getAxiosConfig()
          );
          showMessage('Registration canceled', 'success');
          fetchCamps();
          fetchMyRegistrations();
        } catch (error) {
          showMessage(error.response?.data?.detail || 'Error canceling', 'error');
        }
      }
    });
  };

  // Live slug preview. Computed by the SERVER, not here: the rule is
  // slugify -> append the camp's year -> append -2/-3 on collision, and a
  // browser copy could neither match slugify()'s punctuation handling nor see
  // collisions at all.
  const [slugPreview, setSlugPreview] = useState(null);
  useEffect(() => {
    if (!showCreateModal) { setSlugPreview(null); return undefined; }
    const basis = campForm.slug || campForm.name;
    if (!basis || !basis.trim()) { setSlugPreview(null); return undefined; }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      axios.get(`${API_URL}/camps/slug-preview`, {
        ...getAxiosConfig(),
        signal: controller.signal,
        params: {
          name: campForm.name || '',
          slug: campForm.slug || '',
          date: campForm.date || '',
          ...(editingCamp ? { camp_id: editingCamp.id } : {}),
        },
      })
        .then((r) => setSlugPreview(r.data))
        // An aborted request is the expected outcome of typing, not an error.
        .catch(() => {});
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [campForm.name, campForm.slug, campForm.date, showCreateModal, editingCamp, API_URL]);

  // Price options typed while CREATING a camp, before it has an id to hang them
  // on. POSTed immediately after the camp is created, in order.
  const [pendingOptions, setPendingOptions] = useState([]);

  const handleCreateCamp = async () => {
    if (campForm.end_date && campForm.date && campForm.end_date < campForm.date) {
      showMessage('End date cannot be earlier than start date', 'error');
      return;
    }
    try {
      const priceCents = dollarsToCents(campForm.price_dollars);
      if (priceCents === null) {
        showMessage('Enter a base price in dollars, e.g. 135 or 135.00', 'error');
        return;
      }
      const { price_dollars: _unusedPriceDollars, ...campFormForApi } = campForm;
      const payload = {
        ...campFormForApi,
        price_cents: priceCents,
        capacity: parseInt(campForm.capacity) || 0,
        free_sub_months: parseInt(campForm.free_sub_months) || 0,
        public_registration_enabled: !!campForm.public_registration_enabled,
        ath_option_enabled: !!campForm.ath_option_enabled,
        sport_slug: campForm.sport_slug || null,
      };
      if (editingCamp) {
        await axios.put(`${API_URL}/camps/${editingCamp.id}`, payload, getAxiosConfig());
        showMessage('Camp updated!', 'success');
      } else {
        const created = await axios.post(`${API_URL}/camps`, payload, getAxiosConfig());
        const newId = created?.data?.id;
        const failed = [];
        // Sequential, not Promise.all: sort_order is meaningful and the admin
        // typed these in an order they meant.
        for (const row of pendingOptions) {
          const cents = dollarsToCents(row.price_dollars);
          if (cents === null) { failed.push(row.name || 'unnamed option'); continue; }
          try {
            await axios.post(`${API_URL}/camps/${newId}/price-options`, {
              name: row.name,
              description: row.description || null,
              price_cents: cents,
              position_slugs: row.position_slugs?.length ? row.position_slugs : null,
              sales_start_at: null,
              sales_end_at: null,
              capacity: row.capacity === '' || row.capacity === null
                ? null : parseInt(row.capacity, 10),
              sort_order: parseInt(row.sort_order, 10) || 0,
              is_active: !!row.is_active,
            }, getAxiosConfig());
          } catch {
            failed.push(row.name || 'unnamed option');
          }
        }
        if (failed.length) {
          // The camp exists and is valid, so it is NOT rolled back. Say exactly
          // which rows did not save -- silently dropping them would leave the
          // admin believing the camp is priced when it is not.
          showMessage(
            `Camp created, but these price options did not save: ${failed.join(', ')}. `
            + 'Open the camp and add them again.', 'error');
        } else {
          showMessage('Camp created!', 'success');
        }
      }
      setShowCreateModal(false);
      setEditingCamp(null);
      setPendingOptions([]);
      resetForm();
      fetchCamps();
    } catch (error) {
      showMessage(error.response?.data?.detail || 'Error saving camp', 'error');
    }
  };

  const handleEditCamp = (camp) => {
    setCampForm({
      name: camp.name || '',
      description: camp.description || '',
      location: camp.location || '',
      venue: camp.venue || '',
      date: camp.date ? camp.date.slice(0, 16) : '',
      end_date: camp.end_date ? camp.end_date.slice(0, 16) : '',
      price_dollars: centsToDollars(camp.price_cents),
      capacity: camp.capacity || '',
      event_type: camp.event_type || 'camp',
      timezone: camp.timezone || BROWSER_TZ,
      sport_slug: camp.sport_slug || '',
      slug: camp.slug || '',
      free_sub_months: camp.free_sub_months || 0,
      public_registration_enabled: !!camp.public_registration_enabled,
      ath_option_enabled: !!camp.ath_option_enabled,
    });
    setEditingCamp(camp);
    setShowCreateModal(true);
  };

  const handleDeleteCamp = (campId) => {
    setConfirmAction({
      title: 'Delete Camp',
      message: 'Are you sure you want to delete this camp?',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          await axios.delete(`${API_URL}/camps/${campId}`, getAxiosConfig());
          showMessage('Camp deleted', 'success');
          fetchCamps();
        } catch (error) {
          showMessage(error.response?.data?.detail || 'Error deleting camp', 'error');
        }
      }
    });
  };

  const openRegistrations = async (camp) => {
    setRegsCamp(camp);
    setRegs([]);
    try {
      const res = await axios.get(`${API_URL}/camps/${camp.id}/registrations`, getAxiosConfig());
      setRegs(res.data || []);
    } catch (e) { showMessage(e.response?.data?.detail || 'Failed to load registrations', 'error'); }
  };

  const toggleCheckIn = async (reg) => {
    try {
      const res = await axios.post(`${API_URL}/camps/registrations/${reg.id}/check-in`,
        { attended: !reg.attended_at }, getAxiosConfig());
      setRegs((prev) => prev.map((r) => r.id === reg.id ? { ...r, attended_at: res.data.attended_at } : r));
    } catch (e) { showMessage(e.response?.data?.detail || 'Check-in failed', 'error'); }
  };

  const saveJersey = async (reg, rawValue) => {
    const trimmed = String(rawValue).trim();
    const jersey_number = trimmed === '' ? null : Number(trimmed);
    if (jersey_number !== null && (!Number.isInteger(jersey_number) || jersey_number < 0 || jersey_number > 999)) {
      showMessage('Jersey number must be 0–999', 'error');
      return;
    }
    if (jersey_number === (reg.jersey_number ?? null)) return; // no change
    // Client-side duplicate warning (race-safe backstop is the backend 409).
    let force = false;
    if (jersey_number !== null) {
      const clash = regs.find((r) => r.id !== reg.id && r.jersey_number === jersey_number);
      if (clash && !window.confirm(`#${jersey_number} is already assigned to ${clash.user_name}. Assign anyway?`)) {
        setJerseyResetNonce((n) => n + 1); // force jersey inputs to re-mount to stored values
        return;
      }
      force = !!clash;
    }
    try {
      const res = await axios.patch(`${API_URL}/camps/registrations/${reg.id}/jersey`,
        { jersey_number, force }, getAxiosConfig());
      setRegs((prev) => prev.map((r) => r.id === reg.id ? { ...r, jersey_number: res.data.jersey_number } : r));
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = (detail && detail.message) || detail || 'Failed to save jersey';
      showMessage(msg, 'error');
      setJerseyResetNonce((n) => n + 1); // force jersey inputs to re-mount to stored values
    }
  };

  const submitWalkIn = async () => {
    if (!walkIn.first_name.trim() || !walkIn.last_name.trim() || !walkIn.email.trim()) {
      showMessage('First name, last name, and email are required', 'error');
      return;
    }
    const jersey_number = walkIn.jersey_number === '' ? null : Number(walkIn.jersey_number);
    if (jersey_number !== null) {
      const clash = regs.find((r) => r.jersey_number === jersey_number);
      if (clash && !window.confirm(`#${jersey_number} is already assigned to ${clash.user_name}. Assign anyway?`)) {
        return;
      }
    }
    const payload = {
      first_name: walkIn.first_name.trim(),
      last_name: walkIn.last_name.trim(),
      email: walkIn.email.trim(),
      graduation_year: walkIn.graduation_year === '' ? null : Number(walkIn.graduation_year),
      high_school: walkIn.high_school.trim() || null,
      position: walkIn.position.trim() || null,
      jersey_number,
    };
    try {
      const res = await axios.post(`${API_URL}/camps/${regsCamp.id}/walk-in`, payload, getAxiosConfig());
      // prepend, or replace if an existing registration row was returned (idempotent reuse)
      setRegs((prev) => {
        const without = prev.filter((r) => r.id !== res.data.id);
        return [res.data, ...without];
      });
      setWalkIn(null);
      showMessage('Walk-in checked in', 'success');
    } catch (e) {
      showMessage(e.response?.data?.detail || 'Walk-in failed', 'error');
    }
  };

  const exportRegistrations = async (camp) => {
    try {
      const res = await axios.get(`${API_URL}/camps/${camp.id}/registrations/export`,
        { ...getAxiosConfig(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${camp.slug || camp.name}_registrations.csv`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { showMessage(e.response?.data?.detail || 'Export failed', 'error'); }
  };

  const resetForm = () => {
    setCampForm({
      name: '', description: '', location: '', venue: '',
      date: '', end_date: '', price_dollars: '', capacity: '',
      event_type: 'camp', timezone: BROWSER_TZ, sport_slug: '',
      slug: '', free_sub_months: 0, public_registration_enabled: false,
      ath_option_enabled: false,
    });
  };

  const formatPrice = (cents) => {
    if (!cents || cents === 0) return 'Free';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const isRegistered = (campId) => {
    return myRegistrations.some(r => r.camp_id === campId && r.status === 'confirmed');
  };

  // Escape key handlers for modals
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') setShowContactModal(false); };
    if (showContactModal) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [showContactModal]);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') { setShowCreateModal(false); setEditingCamp(null); } };
    if (showCreateModal) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [showCreateModal]);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') { setRegsCamp(null); setShowImport(false); } };
    if (regsCamp) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [regsCamp]);

  if (loading) {
    if (isAdmin || isPartner) return <RailFrame active="camps"><p className="adm-loading">Loading camps…</p></RailFrame>;
    return <div className="ed-page" style={{ padding: 40 }}>Loading camps...</div>;
  }

  const heroAccent = isAthlete ? 'camp season.' : 'clinics.';
  const heroLead = isAthlete ? 'The road runs through' : 'Camps and';

  const renderCampCard = (camp) => {
    const { month: monthAbbr, day: dayNum } = campTileParts(camp.date, camp.timezone);
    const variant = datetileVariant(camp.id);
    const canEdit = isAdmin || (isPartner && camp.created_by === user?.id);
    const editProps = canEdit ? {
      role: 'button',
      tabIndex: 0,
      'aria-label': `Edit ${camp.name}`,
      onClick: () => handleEditCamp(camp),
      onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleEditCamp(camp); } },
    } : {};
    const isHighlighted = camp.id === highlightedCampId;
    return (
      <div
        key={camp.id}
        data-testid={`camp-card-${camp.id}`}
        className={`ed-card${isHighlighted ? ' ed-card--highlighted' : ''}`}
        style={{
          display: 'flex', flexDirection: 'column', gap: 16,
          ...(canEdit ? { cursor: 'pointer' } : {}),
          ...(isHighlighted ? { outline: '3px solid var(--ed-accent-blue, #0D2133)', outlineOffset: 2 } : {}),
        }}
        ref={(el) => {
          if (isHighlighted && el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }}
        {...editProps}
      >
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <DateTile month={monthAbbr} day={dayNum} variant={variant} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
              <MonoLabel>{(camp.event_type || 'camp').toUpperCase()}{camp.location ? ` · ${camp.location.toUpperCase()}` : ''}</MonoLabel>
              {canEdit && <MonoLabel accent>✎ EDIT</MonoLabel>}
            </div>
            <h3 className="ed-card__title" style={{ marginTop: 4, marginBottom: 6 }}>{camp.name}</h3>
            {camp.venue && <div className="ed-list__row-sub">{camp.venue}</div>}
            {!isCoach && (
              <div style={{ marginTop: 8 }}>
                {camp.spots_remaining > 0
                  ? <Pill variant="green">{camp.spots_remaining} SPOTS LEFT</Pill>
                  : <Pill variant="red">SOLD OUT</Pill>}
              </div>
            )}
          </div>
        </div>

        {camp.description && <p className="ed-card__body">{camp.description}</p>}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <MonoLabel>DATE</MonoLabel>
            <div style={{ fontFamily: 'var(--ed-font-serif)', fontSize: 16, marginTop: 4 }}>{fmtCampDate(camp.date, camp.timezone)}</div>
            <div className="ed-list__row-sub">{fmtCampTime(camp.date, camp.timezone)}</div>
            {camp.end_date && camp.end_date !== camp.date && (
              <div className="ed-list__row-sub">to {fmtCampDate(camp.end_date, camp.timezone)}</div>
            )}
          </div>
          {!isCoach && (
            <div>
              <MonoLabel>PRICE</MonoLabel>
              <div style={{ fontFamily: 'var(--ed-font-serif)', fontSize: 22, fontWeight: 500, marginTop: 4 }}>{formatPrice(camp.price_cents)}</div>
            </div>
          )}
          <div>
            <MonoLabel>{isCoach ? 'REGISTERED' : 'CAPACITY'}</MonoLabel>
            <div style={{ fontFamily: 'var(--ed-font-serif)', fontSize: 16, marginTop: 4 }}>
              {isCoach ? camp.registered_count : `${camp.registered_count} / ${camp.capacity}`}
            </div>
          </div>
        </div>

        <div className="ed-row" style={{ flexWrap: 'wrap', gap: 8 }}>
          {isRegistered(camp.id) ? (
            <EditorialButton variant="ghost" onClick={(e) => {
              e.stopPropagation();
              const reg = myRegistrations.find(r => r.camp_id === camp.id && (r.status === 'confirmed' || r.status === 'pending'));
              if (reg) handleCancelRegistration(reg.id, camp.name);
            }}>
              Cancel Registration
            </EditorialButton>
          ) : (isCoach || camp.spots_remaining > 0) ? (
            <EditorialButton variant="light" onClick={(e) => { e.stopPropagation(); handleRegister(camp.id); }}>
              Register Now
            </EditorialButton>
          ) : (
            <EditorialButton variant="ghost" onClick={(e) => e.stopPropagation()} style={{ opacity: 0.5, cursor: 'not-allowed' }}>
              Sold Out
            </EditorialButton>
          )}
          {canEdit && (
            <>
              <EditorialButton variant="ghost" onClick={(e) => { e.stopPropagation(); handleEditCamp(camp); }}>Edit</EditorialButton>
              <EditorialButton variant="ghost" onClick={(e) => { e.stopPropagation(); handleDeleteCamp(camp.id); }} style={{ color: 'var(--ed-accent-red)' }}>Delete</EditorialButton>
              {isAdmin && (
                <EditorialButton variant="ghost" onClick={(e) => { e.stopPropagation(); openRegistrations(camp); }}>Registrations</EditorialButton>
              )}
            </>
          )}
        </div>
      </div>
    );
  };


  const modalsJsx = (
    <>
      {/* Contact Modal */}
      {showContactModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="contact-modal-title" onClick={() => setShowContactModal(false)}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowContactModal(false)}>&times;</button>
            <div className="modal-title" id="contact-modal-title">Contact ProZpects</div>

            <div className="form-field" style={{ marginBottom: '12px' }}>
              <label className="form-field__label">Name *</label>
              <input
                type="text"
                className="form-field__input"
                value={contactForm.name}
                onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
              />
            </div>
            <div className="form-field" style={{ marginBottom: '12px' }}>
              <label className="form-field__label">Email *</label>
              <input
                type="email"
                className="form-field__input"
                value={contactForm.email}
                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
              />
            </div>
            <div className="form-field" style={{ marginBottom: '12px' }}>
              <label className="form-field__label">Message</label>
              <textarea
                className="form-field__input"
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                rows={4}
                style={{ resize: 'vertical' }}
                placeholder="Tell us what you're looking for..."
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
              <Button variant="primary" onClick={async () => {
                try {
                  await axios.post(`${API_URL}/contact/partner-inquiry`, contactForm, getAxiosConfig());
                  setShowContactModal(false);
                  showMessage('Your inquiry has been submitted!', 'success');
                } catch (error) {
                  showMessage(error.response?.data?.detail || 'Error submitting inquiry', 'error');
                }
              }}>
                Submit
              </Button>
              <Button variant="secondary" onClick={() => setShowContactModal(false)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {confirmAction && (
        <ConfirmModal
          title={confirmAction.title}
          message={confirmAction.message}
          confirmLabel={confirmAction.confirmLabel}
          danger={confirmAction.danger}
          onConfirm={confirmAction.onConfirm}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      {/* Registrations Modal (admin only) */}
      {regsCamp && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="regs-modal-title" onClick={() => { setRegsCamp(null); setWalkIn(null); setShowImport(false); }}>
          <div className="modal-panel" style={{ maxWidth: 960, width: '95vw' }} onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => { setRegsCamp(null); setWalkIn(null); setShowImport(false); }}>&times;</button>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div className="modal-title" id="regs-modal-title" style={{ margin: 0 }}>
                {regsCamp.name} — Registrations ({regs.length})
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="secondary" onClick={() => setWalkIn(walkIn ? null : { ...EMPTY_WALKIN })}>
                  {walkIn ? 'Close Walk-in' : 'Add Walk-in'}
                </Button>
                <Button variant="secondary" onClick={() => exportRegistrations(regsCamp)}>Export CSV</Button>
                {isStrictAdmin && (
                  <Button variant="secondary" onClick={() => setShowImport((v) => !v)}>
                    {showImport ? 'Close Import' : 'Import Roster'}
                  </Button>
                )}
              </div>
            </div>
            {isStrictAdmin && showImport && (
              <CampImportPanel
                camp={regsCamp}
                onImported={() => openRegistrations(regsCamp)}
              />
            )}
            {walkIn && (
              <div style={{ border: '1px solid var(--ed-separator, #e2e8f0)', borderRadius: 8, padding: 12, marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
                {[
                  ['first_name', 'First name *', 'text'],
                  ['last_name', 'Last name *', 'text'],
                  ['email', 'Email *', 'email'],
                  ['graduation_year', 'Grad year', 'number'],
                  ['high_school', 'High school', 'text'],
                  ['position', 'Position', 'text'],
                  ['jersey_number', 'Jersey', 'number'],
                ].map(([field, label, type]) => (
                  <div key={field} style={{ display: 'flex', flexDirection: 'column' }}>
                    <label style={{ fontSize: 11, fontWeight: 600, marginBottom: 2 }}>{label}</label>
                    <input
                      type={type}
                      value={walkIn[field]}
                      onChange={(e) => setWalkIn((w) => ({ ...w, [field]: e.target.value }))}
                      aria-label={label}
                      style={{ padding: '6px 8px', width: field === 'email' ? 200 : (field === 'jersey_number' || field === 'graduation_year' ? 80 : 130) }}
                    />
                  </div>
                ))}
                <Button variant="primary" onClick={submitWalkIn}>Check In Walk-in</Button>
              </div>
            )}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--ed-separator, #e2e8f0)', textAlign: 'left' }}>
                    <th style={{ padding: '8px 10px', fontWeight: 600 }}>Attended</th>
                    <th style={{ padding: '8px 10px', fontWeight: 600 }}>Jersey</th>
                    <th style={{ padding: '8px 10px', fontWeight: 600 }}>Name</th>
                    <th style={{ padding: '8px 10px', fontWeight: 600 }}>Position</th>
                    <th style={{ padding: '8px 10px', fontWeight: 600 }}>ATH</th>
                    <th style={{ padding: '8px 10px', fontWeight: 600 }}>Grad</th>
                    <th style={{ padding: '8px 10px', fontWeight: 600 }}>High School</th>
                    <th style={{ padding: '8px 10px', fontWeight: 600 }}>Email</th>
                    <th style={{ padding: '8px 10px', fontWeight: 600 }}>Phone</th>
                    <th style={{ padding: '8px 10px', fontWeight: 600 }}>Status</th>
                    <th style={{ padding: '8px 10px', fontWeight: 600 }}>Shirt</th>
                    <th style={{ padding: '8px 10px', fontWeight: 600 }}>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {regs.map((r) => (
                    <tr key={r.id} style={{ borderBottom: '1px solid var(--ed-separator, #e2e8f0)' }}>
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={!!r.attended_at}
                          onChange={() => toggleCheckIn(r)}
                          aria-label={`Mark ${r.user_name} as attended`}
                        />
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                        <input
                          type="number"
                          min={0}
                          max={999}
                          step={1}
                          defaultValue={r.jersey_number ?? ''}
                          key={`jersey-${r.id}-${r.jersey_number ?? ''}-${jerseyResetNonce}`}
                          onBlur={(e) => saveJersey(r, e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                          aria-label={`Jersey number for ${r.user_name}`}
                          style={{ width: 64, padding: '4px 6px', textAlign: 'center' }}
                        />
                      </td>
                      <td style={{ padding: '8px 10px' }}>{r.user_name}</td>
                      <td style={{ padding: '8px 10px' }}>{r.position || ''}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>{r.two_way_athlete ? 'ATH' : ''}</td>
                      <td style={{ padding: '8px 10px' }}>{r.graduation_year || ''}</td>
                      <td style={{ padding: '8px 10px' }}>{r.high_school || ''}</td>
                      <td style={{ padding: '8px 10px' }}>{r.user_email || ''}</td>
                      <td style={{ padding: '8px 10px' }}>{r.phone || ''}</td>
                      <td style={{ padding: '8px 10px' }}>{r.status}</td>
                      <td style={{ padding: '8px 10px' }}>{r.tshirt_size || ''}</td>
                      <td style={{ padding: '8px 10px' }}>{r.registration_source === 'walk_in' ? 'Walk-in' : (r.registration_source || '')}</td>
                    </tr>
                  ))}
                  {regs.length === 0 && (
                    <tr>
                      <td colSpan={12} style={{ textAlign: 'center', padding: 24, color: '#64748b' }}>
                        No registrations yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Camp Modal */}
      {showCreateModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="camp-modal-title" onClick={() => { setShowCreateModal(false); setEditingCamp(null); }}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => { setShowCreateModal(false); setEditingCamp(null); }}>
              &times;
            </button>
            <div className="modal-title" id="camp-modal-title">
              {editingCamp
                ? `Edit ${(editingCamp.event_type || 'camp') === 'clinic' ? 'Clinic' : 'Camp'}`
                : `Create ${campForm.event_type === 'clinic' ? 'Clinic' : 'Camp'}`}
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="form-field__label">Type *</label>
                <select
                  className="form-field__input"
                  value={campForm.event_type}
                  onChange={(e) => setCampForm({ ...campForm, event_type: e.target.value })}
                >
                  <option value="camp">Camp</option>
                  <option value="clinic">Clinic</option>
                </select>
              </div>
              <div className="form-field">
                <label className="form-field__label">Sport</label>
                <select
                  className="form-field__input"
                  value={campForm.sport_slug}
                  onChange={(e) => setCampForm({ ...campForm, sport_slug: e.target.value })}
                >
                  <option value="">— none —</option>
                  {sports.map((s) => (
                    <option key={s.slug} value={s.slug}>{s.name}</option>
                  ))}
                </select>
                <span style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
                  Drives the position picker in price options below.
                </span>
              </div>
            </div>

            <div className="form-row form-row--full">
              <div className="form-field">
                <label className="form-field__label">Name *</label>
                <input
                  type="text"
                  className="form-field__input"
                  value={campForm.name}
                  onChange={(e) => setCampForm({ ...campForm, name: e.target.value })}
                  placeholder={campForm.event_type === 'clinic' ? 'Spring QB Clinic 2026' : 'Spring Combine 2026'}
                />
              </div>
            </div>

            <div className="form-row form-row--full">
              <div className="form-field">
                <label className="form-field__label">Description</label>
                <textarea
                  className="form-field__input"
                  value={campForm.description}
                  onChange={(e) => setCampForm({ ...campForm, description: e.target.value })}
                  placeholder="Details about the camp..."
                  rows={3}
                  style={{ resize: 'vertical' }}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="form-field__label">Location *</label>
                <input
                  type="text"
                  className="form-field__input"
                  value={campForm.location}
                  onChange={(e) => setCampForm({ ...campForm, location: e.target.value })}
                  placeholder="Dallas, TX"
                />
              </div>
              <div className="form-field">
                <label className="form-field__label">Venue</label>
                <input
                  type="text"
                  className="form-field__input"
                  value={campForm.venue}
                  onChange={(e) => setCampForm({ ...campForm, venue: e.target.value })}
                  placeholder="AT&T Stadium"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="form-field__label">Start Date *</label>
                <input
                  type="datetime-local"
                  className="form-field__input"
                  value={campForm.date}
                  onChange={(e) => setCampForm({ ...campForm, date: e.target.value })}
                />
              </div>
              <div className="form-field">
                <label className="form-field__label">End Date</label>
                <input
                  type="datetime-local"
                  className="form-field__input"
                  value={campForm.end_date}
                  min={campForm.date || undefined}
                  onChange={(e) => setCampForm({ ...campForm, end_date: e.target.value })}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="form-field__label">Timezone *</label>
                <select
                  className="form-field__input"
                  value={campForm.timezone}
                  onChange={(e) => setCampForm({ ...campForm, timezone: e.target.value })}
                >
                  {campForm.timezone && !US_TIMEZONES.some(z => z.id === campForm.timezone) && (
                    <option value={campForm.timezone}>{campForm.timezone}</option>
                  )}
                  {US_TIMEZONES.map(z => (
                    <option key={z.id} value={z.id}>{z.label}</option>
                  ))}
                </select>
                <span style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
                  The camp's local time — shown in this zone to everyone, nationwide.
                </span>
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="form-field__label">Base price ($) *</label>
                <input
                  type="text"
                  inputMode="decimal"
                  className="form-field__input"
                  value={campForm.price_dollars}
                  onChange={(e) => setCampForm({ ...campForm, price_dollars: e.target.value })}
                  placeholder="135.00"
                />
                <span style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
                  Charged when this camp has no price options below.
                </span>
              </div>
              <div className="form-field">
                <label className="form-field__label">Capacity *</label>
                <input
                  type="number"
                  className="form-field__input"
                  value={campForm.capacity}
                  onChange={(e) => setCampForm({ ...campForm, capacity: e.target.value })}
                  placeholder="50"
                  min="1"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="form-field__label">URL Slug</label>
                <input type="text" className="form-field__input" value={campForm.slug}
                  onChange={(e) => setCampForm({ ...campForm, slug: e.target.value })}
                  placeholder="auto from name if blank (e.g. cod-2026)" />
                {slugPreview && slugPreview.slug && (
                  <span style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}
                        data-testid="camp_slug_preview">
                    {`${window.location.origin}/camp/${slugPreview.slug}`}
                    {slugPreview.collision && ' — that URL is taken, so a number was added'}
                  </span>
                )}
              </div>
              <div className="form-field">
                <label className="form-field__label">Free app months (0–12)</label>
                <input type="number" min="0" max="12" className="form-field__input"
                  value={campForm.free_sub_months}
                  onChange={(e) => setCampForm({ ...campForm, free_sub_months: e.target.value })} />
                <span style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
                  Months of free ProZpects app access included with registration.
                </span>
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="form-field__label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={!!campForm.public_registration_enabled}
                    onChange={(e) => setCampForm({ ...campForm, public_registration_enabled: e.target.checked })} />
                  Public registration enabled
                </label>
                {editingCamp && campForm.slug && campForm.public_registration_enabled && (
                  <div style={{ marginTop: 8, fontSize: 13 }}>
                    Public link:&nbsp;
                    <code>{`${window.location.origin}/camp/${campForm.slug}`}</code>
                    <Button variant="secondary" size="sm" style={{ marginLeft: 8 }}
                      onClick={() => navigator.clipboard?.writeText(`${window.location.origin}/camp/${campForm.slug}`)}>
                      Copy
                    </Button>
                  </div>
                )}
              </div>
              <div className="form-field">
                <label className="form-field__label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={!!campForm.ath_option_enabled}
                    data-testid="camp_ath_option_enabled"
                    onChange={(e) => setCampForm({ ...campForm, ath_option_enabled: e.target.checked })} />
                  Offer ATH (two-way) checkbox
                </label>
                <span style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
                  Adds “ATH — I intend to be tested on offense and defense” under the
                  position on the public registration form. It does not change the price.
                </span>
              </div>
            </div>

            <div className="form-row form-row--full">
              <div className="form-field">
                <label className="form-field__label">Price options</label>
                <CampPriceOptionsEditor campId={editingCamp?.id}
                                        sportSlug={campForm.sport_slug || null}
                                        value={pendingOptions}
                                        onChange={setPendingOptions} />
              </div>
            </div>

            <div className="form-row form-row--full">
              <div className="form-field">
                <label className="form-field__label">Description page</label>
                <CampPageContentEditor campId={editingCamp?.id} slug={campForm.slug} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <Button variant="primary" onClick={handleCreateCamp}>
                {editingCamp ? 'Save Changes' : 'Create Camp'}
              </Button>
              <Button variant="secondary" onClick={() => { setShowCreateModal(false); setEditingCamp(null); }}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // ---- Admin / staff / partner: the table view inside the rail (2a / 2b) ----
  if (isAdmin || isPartner) {
    const nowMs = Date.now();
    const endMs = (c) => new Date(c.end_date || c.date).getTime();
    const isPastCamp = (c) => endMs(c) < nowMs;
    const upcoming = camps.filter((c) => !isPastCamp(c));
    const dollars = (cents) => `$${Math.round(cents / 100).toLocaleString('en-US')}`;
    // revenue_cents comes from the API (staff only): what registrants actually paid through Stripe.
    const campRevenue = (c) => c.revenue_cents || 0;
    const registered = upcoming.reduce((n, c) => n + (c.registered_count || 0), 0);
    const capacity = upcoming.reduce((n, c) => n + (c.capacity || 0), 0);
    const fill = capacity > 0 ? Math.round((registered / capacity) * 100) : 0;
    const revenue = upcoming.reduce((n, c) => n + campRevenue(c), 0);
    const nextUp = upcoming.slice().sort((a, b) => new Date(a.date) - new Date(b.date))[0];
    const nextUpLabel = nextUp
      ? `${campTileParts(nextUp.date, nextUp.timezone).month.charAt(0)}${campTileParts(nextUp.date, nextUp.timezone).month.slice(1).toLowerCase()} ${campTileParts(nextUp.date, nextUp.timezone).day} · ${(nextUp.location || nextUp.name).split(',')[0]}`
      : '—';

    // Admin Past: per-camp review signals from GET /camps/review-summary. A camp missing from the
    // map, and every camp while the summary is loading or failed, reads — with plain actions.
    const pastSignals = isAdmin && when === 'past';
    const sig = (c) => (reviewSummary ? reviewSummary.camps[c.id] || null : null);
    const openFlags = (s) => (s ? (s.flags?.lost || 0) + (s.flags?.warning || 0) : 0);
    const tolerance = reviewSummary?.tolerance_minutes;
    const past = camps.filter(isPastCamp);
    const past90 = past.filter((c) => nowMs - new Date(c.date).getTime() <= 90 * 86400000);
    const pastRevenue = past90.reduce((n, c) => n + campRevenue(c), 0);
    const pastSigs = past.map(sig).filter(Boolean);
    // Imported camps carry results but no saves of their own, so they sit out Measured and On schedule.
    const liveSigs = pastSigs.filter((s) => !s.imported);
    const measuredN = liveSigs.reduce((n, s) => n + (s.measured || 0), 0);
    const registeredN = liveSigs.reduce((n, s) => n + (s.registered || 0), 0);
    const measuredPct = registeredN > 0 ? Math.round((measuredN / registeredN) * 100) : 0;
    const flagsN = pastSigs.reduce((n, s) => n + openFlags(s), 0);
    const lostN = pastSigs.reduce((n, s) => n + (s.flags?.lost || 0), 0);
    const lostCamps = pastSigs.filter((s) => (s.flags?.lost || 0) > 0).length;
    const timedSigs = liveSigs.filter((s) => s.start_offset_minutes !== null && s.start_offset_minutes !== undefined);
    const onTimeN = timedSigs.filter((s) => Math.abs(s.start_offset_minutes) <= tolerance).length;

    const owners = Array.from(new Set(camps.map((c) => c.created_by).filter((v) => v != null)));
    const q = campFind.trim().toLowerCase();
    const rows = camps
      .filter((c) => (when === 'past' ? isPastCamp(c) : !isPastCamp(c)))
      .filter((c) => kind === 'all' || (c.event_type || 'camp') === kind)
      .filter((c) => partnerFilter === 'all' || String(c.created_by) === partnerFilter)
      .filter((c) => !q || `${c.name} ${c.venue || ''} ${c.location || ''}`.toLowerCase().includes(q))
      .filter((c) => !(pastSignals && needsReview) || openFlags(sig(c)) > 0)
      .sort((a, b) => (when === 'past' ? new Date(b.date) - new Date(a.date) : new Date(a.date) - new Date(b.date)));

    const openCreate = (eventType) => {
      resetForm();
      setCampForm((f) => ({ ...f, event_type: eventType, }));
      setEditingCamp(null);
      setShowCreateModal(true);
    };

    const heroTile = (label, value, sub) => (
      <div className="adm-kpi adm-kpi--hero">
        <span className="adm-kpi__label">{label}</span>
        <div className="adm-kpi__value">{value}</div>
        {sub && <div className="adm-kpi__sub camp-kpi__sub--onhero">{sub}</div>}
      </div>
    );
    const tile = (label, value, sub, textValue = false, valueClass = '') => (
      <div className="adm__card adm-kpi">
        <span className="adm-kpi__label adm-kpi__label--dark">{label}</span>
        <div className={`adm-kpi__value adm-kpi__value--dark ${textValue ? 'camp-kpi__value--text' : ''} ${valueClass}`}>{value}</div>
        {sub && <div className="adm-kpi__sub">{sub}</div>}
      </div>
    );

    const dash = <span className="camp-muted">—</span>;
    const measuredCell = (s) => {
      if (!s) return dash;
      if (s.imported) {
        return (
          <>
            <span className="camp-reg">{s.measured} results <span className="camp-muted">imported</span></span>
            <progress className="camp-bar camp-bar--imported" max={1} value={1} aria-label="results imported" />
          </>
        );
      }
      const r = s.registered || 0;
      const pct = r > 0 ? Math.round((s.measured / r) * 100) : 0;
      return (
        <>
          <span className="camp-reg">{s.measured} / {r}{r > 0 && <span className="camp-muted"> · {pct}%</span>}</span>
          <progress className={`camp-bar${pct < 50 ? ' camp-bar--amber' : ''}`} max={r || 1} value={Math.min(s.measured, r || 1)}
            aria-label={`${pct}% measured`} />
        </>
      );
    };
    const dayCell = (s, tz) => {
      if (s && s.imported) {
        return (<><span className="camp-day">No timing data</span><span className="camp-off camp-off--muted">imported</span></>);
      }
      if (!s || !s.first) return dash;
      const off = offsetLabel(s.start_offset_minutes, tolerance);
      return (
        <>
          <span className="camp-day">{fmtRange(s.first, s.last, tz)}</span>
          <span className={`camp-off camp-off--${off.tone}`}>{off.text}</span>
        </>
      );
    };
    const flagsCell = (s) => {
      if (!s) return dash;
      const lost = s.flags?.lost || 0;
      const warn = s.flags?.warning || 0;
      return (
        <span className="camp-flags">
          {lost > 0 && <span className="adm-chip adm-chip--red">{lost} lost</span>}
          {warn > 0 && <span className="adm-chip adm-chip--amber">{warn} warn</span>}
          {s.imported && <span className="adm-chip">Imported</span>}
          {!s.imported && lost + warn === 0 && <span className="adm-chip adm-chip--green">Clean</span>}
        </span>
      );
    };
    // Admin Past row (camp review v2): the review signals replace TYPE / REGISTERED / PRICE / STATUS.
    // Row click still opens Edit; Review (needs attention) or Open both go to the review page.
    const pastRow = (c, month, day, hi) => {
      const s = sig(c);
      const flagged = openFlags(s) > 0;
      const goReview = (e) => { e.stopPropagation(); navigate(`/camps/${c.id}/review`); };
      return (
        <tr key={c.id} data-testid={`camp-card-${c.id}`}
          className={`camp-row--edit camp-row--past${hi ? ' adm-row--highlighted' : ''}`}
          ref={(el) => { if (hi && el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
          onClick={() => handleEditCamp(c)}>
          <td>
            <span className="camp-date" aria-label={fmtCampDate(c.date, c.timezone)}>
              <span className="camp-date__m">{month}</span>
              <span className="camp-date__d">{day}</span>
            </span>
          </td>
          <td>
            <span className="camp-name">{c.name}</span>
            <span className="camp-sub">
              {[(c.event_type || 'camp') === 'clinic' ? 'Clinic' : 'Camp', c.venue, c.location].filter(Boolean).join(' · ')}
            </span>
          </td>
          <td>{measuredCell(s)}</td>
          <td>{dayCell(s, c.timezone || FALLBACK_TZ)}</td>
          <td>{flagsCell(s)}</td>
          <td className="camp-money">{dollars(campRevenue(c))}</td>
          <td className="camp-actions-cell">
            <button type="button" className="adm-btn adm-btn--sm adm-btn--ghost"
              onClick={(e) => { e.stopPropagation(); openRegistrations(c); }}>Registrations</button>
            {flagged
              ? <button type="button" className="adm-btn adm-btn--sm adm-btn--dark" onClick={goReview}>Review</button>
              : <button type="button" className="adm-btn adm-btn--sm adm-btn--ghost" onClick={goReview}>Open</button>}
          </td>
        </tr>
      );
    };

    return (
      <RailFrame active="camps">
        {message && (
          <div className={`ls-banner ${messageType === 'error' ? 'ls-banner--error' : ''}`} role="status">{message}</div>
        )}
        <header className="adm-header">
          <div>
            <h1 className="adm-header__title">Camps</h1>
            <p className="adm-header__sub">{isAdmin ? 'All camps and clinics, every partner' : 'Your camps and clinics'}</p>
          </div>
          <div className="camp-actions">
            {isAdmin && (
              <button type="button" className="adm-btn" onClick={() => navigate('/camps/checkin')}>Check-in</button>
            )}
            {/* Wristbands left the rail's Events group (it is not an event; David, 2026-10-29) and lives
                here instead. Same gate as the old rail item and the /admin/wristbands route: admin and
                internal_staff, which is what isAdmin means on this page. */}
            {isAdmin && (
              <button type="button" className="adm-btn" onClick={() => navigate('/admin/wristbands')}>Wristbands</button>
            )}
            <button type="button" className="adm-btn" onClick={() => openCreate('clinic')}>+ Clinic</button>
            <button type="button" className="adm-btn adm-btn--primary" onClick={() => openCreate('camp')}>+ Camp</button>
          </div>
        </header>

        <div className="adm-kpis">
          {pastSignals ? (
            <>
              {heroTile('Camp revenue · past 90 days', dollars(pastRevenue), `${past90.length} events · paid via Stripe`)}
              {tile('Measured', reviewSummary ? <>{measuredN}<span className="camp-kpi__of"> / {registeredN}</span></> : '—',
                reviewSummary ? `${measuredPct}% of registered got a stat` : null)}
              {tile('Open flags', reviewSummary ? flagsN : '—',
                reviewSummary ? `${lostN} lost ${lostN === 1 ? 'entry' : 'entries'} across ${lostCamps} ${lostCamps === 1 ? 'camp' : 'camps'}` : null, false,
                reviewSummary && flagsN > 0 ? 'camp-kpi__value--red' : '')}
              {tile('On schedule', reviewSummary ? <>{onTimeN}<span className="camp-kpi__of"> of {timedSigs.length}</span></> : '—',
                reviewSummary ? `started within ${tolerance} min of the plan` : null)}
            </>
          ) : isAdmin ? (
            <>
              {heroTile('Camp revenue · upcoming', dollars(revenue), 'paid via Stripe')}
              {tile('Registered', registered, `across ${upcoming.length} upcoming`)}
              {tile('Fill rate', `${fill}%`, `${registered} of ${capacity} spots`)}
              {tile('Avg. per registrant', registered > 0 ? dollars(revenue / registered) : '—', 'paid ÷ registered')}
            </>
          ) : (
            <>
              {heroTile('Athletes registered', registered, `across ${upcoming.length} upcoming`)}
              {tile('Upcoming events', upcoming.length)}
              {tile('Fill rate', `${fill}%`, `${registered} of ${capacity} spots`)}
              {tile('Next up', nextUpLabel, null, true)}
            </>
          )}
        </div>

        <div className="adm__card adm__card--flush">
          <div className="adm-toolbar adm-toolbar--incard camp-toolbar">
            <AdminWindowSwitcher label="When" value={when} onChange={changeWhen}
              options={[{ value: 'upcoming', label: 'Upcoming' }, { value: 'past', label: 'Past' }]} />
            <AdminWindowSwitcher label="Type" value={kind} onChange={setKind}
              options={[{ value: 'all', label: 'All' }, { value: 'camp', label: 'Camps' }, { value: 'clinic', label: 'Clinics' }]} />
            {pastSignals && (
              <AdminWindowSwitcher label="Review" value={needsReview ? 'needs' : 'any'} onChange={(v) => setNeedsReview(v === 'needs')}
                options={[{ value: 'any', label: 'Any' }, { value: 'needs', label: 'Needs review' }]} />
            )}
            {isAdmin && owners.length > 1 && (
              <select className="adm-select camp-toolbar__partner" aria-label="Partner" value={partnerFilter}
                onChange={(e) => setPartnerFilter(e.target.value)}>
                <option value="all">Partner: All</option>
                {owners.map((id) => <option key={id} value={String(id)}>{id === user?.id ? 'Me' : `Partner #${id}`}</option>)}
              </select>
            )}
            <input className="adm-input adm-toolbar__spacer camp-toolbar__find" type="search" placeholder="Find an event"
              aria-label="Find an event" value={campFind} onChange={(e) => setCampFind(e.target.value)} />
          </div>

          {loadError ? (
            <p className="adm-error" role="alert">Could not load camps.</p>
          ) : rows.length === 0 ? (
            <p className="adm-empty">{camps.length === 0 ? 'No camps or clinics yet.' : 'No events match these filters.'}</p>
          ) : (
            <div className="adm-tablewrap">
              <table className="adm-table">
                <thead>
                  {pastSignals ? (
                    <tr>
                      <th>DATE</th><th>EVENT</th><th>MEASURED</th><th>DAY</th><th>FLAGS</th><th>REVENUE</th>
                      <th><span className="camp-sr">Actions</span></th>
                    </tr>
                  ) : (
                    <tr>
                      <th>DATE</th><th>EVENT</th><th>TYPE</th><th>REGISTERED</th>
                      {isAdmin ? (<><th>PRICE</th><th>REVENUE</th></>) : (<><th>PRICE</th><th>SPOTS</th></>)}
                      <th>STATUS</th><th><span className="camp-sr">Actions</span></th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {rows.map((c) => {
                    const { month, day } = campTileParts(c.date, c.timezone);
                    const canEdit = isAdmin || (isPartner && c.created_by === user?.id);
                    const full = (c.spots_remaining ?? 0) <= 0;
                    const pct = c.capacity > 0 ? Math.min(100, Math.round(((c.registered_count || 0) / c.capacity) * 100)) : 0;
                    const hi = c.id === highlightedCampId;
                    if (pastSignals) return pastRow(c, month, day, hi);
                    return (
                      <tr key={c.id} data-testid={`camp-card-${c.id}`}
                        className={`${canEdit ? 'camp-row--edit' : ''}${hi ? ' adm-row--highlighted' : ''}`}
                        ref={(el) => { if (hi && el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
                        onClick={canEdit ? () => handleEditCamp(c) : undefined}>
                        <td>
                          <span className="camp-date" aria-label={fmtCampDate(c.date, c.timezone)}>
                            <span className="camp-date__m">{month}</span>
                            <span className="camp-date__d">{day}</span>
                          </span>
                        </td>
                        <td>
                          <span className="camp-name">{c.name}</span>
                          <span className="camp-sub">{[c.venue, c.location].filter(Boolean).join(' · ')}</span>
                        </td>
                        <td>{(c.event_type || 'camp') === 'clinic' ? 'Clinic' : 'Camp'}</td>
                        <td>
                          <span className="camp-reg">{c.registered_count} / {c.capacity}</span>
                          <progress className="camp-bar" max={c.capacity || 1} value={Math.min(c.registered_count || 0, c.capacity || 1)} aria-label={`${pct}% full`} />
                        </td>
                        {isAdmin ? (
                          <>
                            <td>{dollars(c.price_cents || 0)}</td>
                            <td className="camp-money">{dollars(campRevenue(c))}</td>
                          </>
                        ) : (
                          <>
                            <td>{dollars(c.price_cents || 0)}</td>
                            <td>{full ? 'Full' : `${c.spots_remaining} left`}</td>
                          </>
                        )}
                        <td>
                          {c.is_active === false
                            ? <span className="adm-chip">Draft</span>
                            : full ? <span className="adm-chip adm-chip--red">Sold out</span>
                            : <span className="adm-chip adm-chip--green">Open</span>}
                        </td>
                        <td className="camp-actions-cell">
                          {isAdmin && (
                            <button type="button" className="adm-btn adm-btn--sm"
                              onClick={(e) => { e.stopPropagation(); openRegistrations(c); }}>Registrations</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {modalsJsx}
      </RailFrame>
    );
  }

  const upcomingMine = myRegistrations.filter(r => r.status === 'confirmed' || r.status === 'pending');

  return (
    <div className="ed-page">
      {message && (
        <div className={`settings-toast ${messageType === 'error' ? 'settings-toast--error' : ''}`}>
          {message}
        </div>
      )}

      <EditorHero
        eyebrow={
          <>
            <MonoLabel onDark>{isAthlete ? 'CAMPS · GET VERIFIED' : 'CAMPS · UPCOMING'}</MonoLabel>
            {isAthlete && lastVerifiedAt && (
              <>
                <MonoLabel onDark>·</MonoLabel>
                <MonoLabel onDark>LAST VERIFIED {new Date(lastVerifiedAt).toLocaleDateString().toUpperCase()}</MonoLabel>
              </>
            )}
          </>
        }
        headline={heroLead}
        accent={heroAccent}
        sub={isAthlete
          ? 'Coaches are 73% more likely to make an offer to athletes with verified data. Camps are how you get there.'
          : 'Browse and register for upcoming ProZpects camps and clinics.'}
        actions={
          (isAdmin || isPartner) ? (
            <>
              {isAdmin && (
                <EditorialButton variant="ghost" onClick={() => navigate('/camps/checkin')}>
                  Check-In
                </EditorialButton>
              )}
              <EditorialButton variant="solid" onClick={() => { resetForm(); setCampForm(f => ({ ...f, event_type: 'camp' })); setEditingCamp(null); setShowCreateModal(true); }}>
                + Create Camp
              </EditorialButton>
              <EditorialButton variant="ghost" onClick={() => { resetForm(); setCampForm(f => ({ ...f, event_type: 'clinic' })); setEditingCamp(null); setShowCreateModal(true); }}>
                + Create Clinic
              </EditorialButton>
            </>
          ) : isAthlete ? (
            <EditorialButton variant="ghost" onClick={() => {
              setContactForm({ name: user ? `${user.first_name} ${user.last_name}` : '', email: user?.email || '', message: '' });
              setShowContactModal(true);
            }}>
              Find a Certified Partner
            </EditorialButton>
          ) : null
        }
      />

      {/* My Registrations */}
      {upcomingMine.length > 0 && (
        <>
          <SectionCap label="My Schedule" hint="UPCOMING" />
          <div className="ed-list">
            {myRegistrations.map(reg => (
              <div key={reg.id} className="ed-list__row">
                <DateTile
                  month={campTileParts(reg.camp_date, reg.camp_timezone).month}
                  day={campTileParts(reg.camp_date, reg.camp_timezone).day}
                  variant={datetileVariant(reg.id)}
                />
                <div style={{ flex: 1 }}>
                  <div className="ed-list__row-title">{reg.camp_name}</div>
                  <div className="ed-list__row-sub">{fmtCampDate(reg.camp_date, reg.camp_timezone)} · {fmtCampTime(reg.camp_date, reg.camp_timezone)} · {reg.camp_location}</div>
                </div>
                <Pill variant={reg.status === 'confirmed' ? 'green' : 'yellow'}>
                  {reg.status === 'confirmed' ? 'Confirmed' : 'Pending'}
                </Pill>
                <EditorialButton variant="ghost" onClick={() => handleCancelRegistration(reg.id, reg.camp_name)}>
                  Cancel
                </EditorialButton>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Camp Cards */}
      {camps.length > 0 ? (
        <>
          {['camp', 'clinic'].map(kind => {
            const items = camps.filter(c => (c.event_type || 'camp') === kind);
            if (items.length === 0) return null;
            const sectionTitle = kind === 'camp' ? 'Camps' : 'Clinics';
            return (
              <div key={kind}>
                <SectionCap label={sectionTitle} hint="EXPLORE" />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
                  {items.map(renderCampCard)}
                </div>
              </div>
            );
          })}
        </>
      ) : (
        <EmptyState
          title="No camps or clinics yet."
          body="Check back soon — new ProZpects camps go up regularly."
        />
      )}

      {modalsJsx}
    </div>
  );
}
